/**
 * Iter89 — Assessment-claim detail page.
 *
 * Deep-linked from notifications (`/assessment-claims/{cid}`) so the coach or
 * admin lands directly on the pending claim they need to decide. Students
 * viewing their own claim see a read-only summary + status.
 *
 * All the real business logic — approve/reject with awarded level — lives in
 * the backend (`POST /assessment-claims/{id}/approve|reject`). This page is
 * just a focused view of that.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Check, X, User as UserIcon, ShieldCheck, Camera } from "lucide-react";

const STATUS_META = {
  pending: { color: "#CCFF00", label: "PENDING" },
  approved: { color: "#22C55E", label: "APPROVED" },
  rejected: { color: "#FF4500", label: "REJECTED" },
};

export default function AssessmentClaimDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [claim, setClaim] = useState(null);
  const [error, setError] = useState(null);
  const [awarded, setAwarded] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/assessment-claims/${id}`)
      .then((r) => {
        setClaim(r.data);
        setAwarded(String(r.data?.prior_level ?? ""));
      })
      .catch((e) => setError(formatApiError(e.response?.data?.detail) || "Couldn't load this claim."));
  }, [id]);

  const decide = async (decision) => {
    setBusy(true);
    try {
      const body = { decision_notes: notes };
      if (decision === "approved") {
        const n = parseFloat(awarded);
        if (Number.isNaN(n)) { toast.error("Please set an awarded level to approve."); setBusy(false); return; }
        body.awarded_level = n;
      }
      // Backend routes are imperative: /approve, /reject (state param is past tense: 'approved'/'rejected').
      const action = decision === "approved" ? "approve" : "reject";
      const { data } = await api.post(`/assessment-claims/${id}/${action}`, body);
      setClaim(data);
      toast.success(decision === "approved" ? "Claim approved" : "Claim rejected");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update the claim");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/dashboard" className="label-mono text-[#A3A3A3] hover:text-white inline-flex items-center gap-2"><ArrowLeft className="w-3 h-3" /> Back to dashboard</Link>
        <div className="card-surface p-8 text-center border border-[#FF4500]" data-testid="claim-error">
          <div className="text-sm text-[#FF4500]">{error}</div>
        </div>
      </div>
    );
  }
  if (!claim) {
    return <div className="label-mono text-[#525252]">Loading claim…</div>;
  }

  const meta = STATUS_META[claim.status] || STATUS_META.pending;
  const isCoach = user?.id === claim.coach_id;
  const isAdmin = user?.role === "admin" || (user?.permissions || []).includes("super_admin");
  const canDecide = claim.status === "pending" && (isCoach || isAdmin);

  return (
    <div className="max-w-2xl space-y-6" data-testid="assessment-claim-detail">
      <Link to="/dashboard" className="label-mono text-[#A3A3A3] hover:text-white inline-flex items-center gap-2">
        <ArrowLeft className="w-3 h-3" /> Back to dashboard
      </Link>

      <div className="card-surface p-6 border-l-4" style={{ borderColor: meta.color }}>
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="label-mono" style={{ color: meta.color }} data-testid="claim-status">{meta.label}</div>
          <div className="text-[10px] uppercase tracking-widest text-[#525252]">
            {new Date(claim.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>
        <h1 className="h-display text-3xl tracking-tight mb-2">Prior {claim.sport} assessment claim</h1>
        <p className="text-sm text-[#A3A3A3]">
          <span className="font-semibold text-white">{claim.student_name || "The student"}</span> is claiming they were previously assessed by <span className="font-semibold text-white">{claim.coach_name || "you"}</span> at level <span className="text-[#CCFF00] font-semibold">{claim.prior_level ?? "—"}</span>.
        </p>

        {(claim.evidence || claim.notes) && (
          <div className="mt-4 border border-[#262626] p-4">
            <div className="label-mono text-[#A3A3A3] mb-1">EVIDENCE / NOTES</div>
            <div className="text-sm text-[#E5E5E5] whitespace-pre-wrap">{claim.evidence || claim.notes}</div>
          </div>
        )}

        {claim.status !== "pending" && (
          <div className="mt-4 border border-[#262626] p-4 space-y-2">
            <div className="label-mono text-[#A3A3A3]">DECISION</div>
            <div className="text-sm">Awarded level: <span className="text-[#CCFF00] font-semibold">{claim.awarded_level ?? "—"}</span></div>
            {claim.decision_notes && <div className="text-xs text-[#A3A3A3] whitespace-pre-wrap">{claim.decision_notes}</div>}
            <div className="text-[10px] uppercase tracking-widest text-[#525252]">
              Decided by {claim.decision_role || "system"} · {claim.decided_at ? new Date(claim.decided_at).toLocaleString() : "—"}
            </div>
          </div>
        )}
      </div>

      {canDecide && (
        <div className="card-surface p-6 space-y-4" data-testid="claim-decision-panel">
          <div className="label-mono text-[#CCFF00]">YOUR DECISION</div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-1">Awarded level (2.0–5.0)</label>
            <input
              type="number" min="2" max="5" step="0.25"
              value={awarded} onChange={(e) => setAwarded(e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] outline-none"
              data-testid="claim-awarded-input"
            />
            <div className="text-[10px] text-[#525252] mt-1">Blank rejects the claim. Any decimal in 0.25 increments works (2.5, 3.75, etc).</div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-[#A3A3A3] block mb-1">Notes (optional, sent to the student)</label>
            <textarea
              rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#141414] border border-[#262626] px-3 py-2 text-sm focus:border-[#CCFF00] outline-none"
              data-testid="claim-notes-input"
              placeholder="e.g. 'Assessed at Court 2 in Aug last year — locked at 3.5'"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              type="button" disabled={busy} onClick={() => decide("approved")}
              className="btn-volt min-h-[44px] px-5 py-2.5 text-xs uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-all"
              data-testid="claim-approve-btn"
            >
              <Check className="w-4 h-4" /> Approve at {awarded || "?"}
            </button>
            <button
              type="button" disabled={busy} onClick={() => decide("rejected")}
              className="min-h-[44px] px-5 py-2.5 text-xs uppercase tracking-wider border border-[#FF4500] text-[#FF4500] hover:bg-[#FF4500] hover:text-black inline-flex items-center gap-2 disabled:opacity-50 active:scale-95 transition-all"
              data-testid="claim-reject-btn"
            >
              <X className="w-4 h-4" /> Reject
            </button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
        <div className="border border-[#262626] p-3 flex gap-3">
          <UserIcon className="w-5 h-5 text-[#525252] shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <div className="label-mono text-[#525252] mb-0.5">STUDENT</div>
            <div className="font-semibold truncate">{claim.student_name || "—"}</div>
          </div>
        </div>
        <div className="border border-[#262626] p-3 flex gap-3">
          <ShieldCheck className="w-5 h-5 text-[#525252] shrink-0" strokeWidth={1.5} />
          <div className="min-w-0">
            <div className="label-mono text-[#525252] mb-0.5">COACH</div>
            <div className="font-semibold truncate">{claim.coach_name || "—"}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
